import React, { useState, useEffect, useRef, useContext } from "react";
import styled from "styled-components";
import { getDateWithOffset, debounce } from "common/api";
import { formatDate } from "views/Statistics/SubscriberDetail/livemetrics.utils";
import {
  getItemsUpdate,
  getHoveredTime,
  getMouseX,
  getValueItems,
  Title,
  LegendContainer,
  CursorLine,
} from "./utils";
import NVStackedAreaChartLive from "common/NVStackedAreaChartLive";
import { default as LiveLegend } from "views/Statistics/SubscriberDetail/DPIAnalyticsLiveLegend";
import Tooltip from "./Tooltip";
import BqnContext from "common/BqnContext";

const Wrapper = styled.div`
  margin-bottom: 25px;
`;

const marginRight = 50;
const marginLeft = 70;
const marginTop = 5;
const marginBottom = 30;
const margin = {
  top: marginTop,
  right: marginRight,
  bottom: marginBottom,
  left: marginLeft,
};

const ChartsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  @media (max-width: 900px) {
    display: block;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  position: relative;

  @media (max-width: 900px) {
    width: 100%;
  }

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }

  .border {
    position: absolute;
    z-index: 99999;

    &.top {
      width: 100%;
      height: 55px;
      min-height: 55px;
      top: -50px;
      left: 0;
    }
    &.right {
      width: 45px;
      height: 100%;
      top: 0;
      right: 5px;
    }

    &.bottom {
      min-height: 70px;
      width: 100%;
      height: 30px;
      bottom: -40px;
      left: 0;
    }

    &.left {
      width: 50px;
      height: 100%;
      top: 0;
      left: 20px;
    }
  }
`;

const LiveChart = ({
  title,
  value,
  fields,
  fieldsMap,
  onMouseEvent,
  mouseEvent,
  timeWindow,
  type,
  filterFields,
  onFilterFieldsChange,
  fixedYRange,
  resetSignal,
  test
}) => {
  const [items, setItems] = useState([]);
  const [window, setWindow] = useState(0);
  const [filteredFields, setFilteredFields] = useState([]);
  const firstValue = useRef(false);
  const fieldsSet = useRef({});
  const dataMap = useRef({});
  const [rangeMain, setRangeMain] = useState(0);
  const [rangeExtra] = useState(0);
  const {
    setBqnId,
  } = useContext(BqnContext);

  const width = rangeMain + marginLeft + marginRight;
  const renderRate = window === 10 ? 2 : 1;
  const valueKeys = ["link"].map((key) => `${type}${key}`);
  const hoverTime = getHoveredTime(mouseEvent, items, rangeMain);
  const { mouseXMain } =
    getMouseX(mouseEvent, rangeMain, rangeExtra) || {};
  const tooltipData = findTooltipData(hoverTime);

  useEffect(() => {
    reset();
  }, [timeWindow, resetSignal]);

  useEffect(() => {
    if (!value) {
      return;
    }

    const now = value.time;
    const newValue = { ...value };

    if (!firstValue.current) {
      firstValue.current = newValue;
    }

    fieldsSet.current = fieldsMap;

    const timeFrame =
      newValue.time.getTime() - firstValue.current.time.getTime();
    let { itemsUpdate, window: windowNew } = getItemsUpdate({
      items,
      now,
      fields,
      timeFrame,
      timeWindow,
      newValue,
      valueKeys,
      window,
    });
    setWindow(windowNew);
    const lastValue = itemsUpdate[itemsUpdate.length - 1];
    if (
      lastValue?.time &&
      newValue?.time &&
      formatDate(lastValue.time) !== formatDate(newValue.time)
    ) {
      delete dataMap.current[formatDate(itemsUpdate[0].time)];
      dataMap.current[formatDate(newValue.time)] = {
        time: newValue.time,
        fields: [...fields],
        value: newValue,
      };
      const newItems = [...itemsUpdate.slice(1), newValue]

      setItems(newItems);
    }
  }, [value]);

  useEffect(() => {
    if (filterFields) {
      setFilteredFields(fields.filter((field) => filterFields.has(field.name)));
    }
  }, [filterFields]);

  function removeTooltip() {
    if (mouseEvent) {
      onMouseEvent(null);
    }
  }

  const createMouseEvent = (e) => {
    const newEvent = {
      ...e,
      rangeMain,
      mouseXMain: e.mouseX,
      mouseXExtra: null,
    };
    onMouseEvent(newEvent);
  };

  function reset() {
    setItems([]);
    setWindow(0);
    firstValue.current = false;
  }

  function findTooltipData(hoverTime) {
    if(!hoverTime){
      return null;
    }

    const oneSecondAfter = new Date(hoverTime.getTime() + 1000);
    const oneSecondBefore = new Date(hoverTime.getTime() - 1000);

    return (
      dataMap.current[formatDate(hoverTime)] ??
      dataMap.current[formatDate(oneSecondBefore)] ??
      dataMap.current[formatDate(oneSecondAfter)]
    );
  }

  return (
    <Wrapper>
      <Title className="chart-title">{title}</Title>
      <ChartsWrapper>
        <ChartContainer>
          {items.length > 0 ? (
            <LegendContainer>
              <LiveLegend
                fields={fields}
                onChange={(filter) => onFilterFieldsChange(filter)}
                value={filterFields}
                rest={false}
                onContextMenu={(name) => setBqnId(name.split(' ')[1])}
                maxWidthField="240"
              />
            </LegendContainer>
          ) : null}
          {mouseEvent && (
            <>
              <div
                className="border top"
                onMouseEnter={removeTooltip}
                onMouseLeave={removeTooltip}
              ></div>
              <div
                className="border right"
                onMouseEnter={removeTooltip}
                onMouseLeave={removeTooltip}
              ></div>
              <div
                className="border bottom"
                onMouseEnter={removeTooltip}
                onMouseLeave={removeTooltip}
              ></div>
              <div
                className="border left"
                onMouseEnter={removeTooltip}
                onMouseLeave={removeTooltip}
              ></div>
            </>
          )}
          <NVStackedAreaChartLive
            items={getValueItems(items, `${type}link`)}
            xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
            fields={filteredFields}
            yAxisUnits="Mbps"
            yAxisTopGap={0.05}
            margin={margin}
            chartWidth={width - margin.left - margin.top}
            minHeight="276px"
            onHighlight={(e) => {
              if (e) {
                debounce(createMouseEvent(e), 2000);
              }
            }}
            onSeriesInspect={({name}) => setBqnId(name.split(' ')[1])}
            onRange={(rangeNew) => {
              if (rangeNew !== rangeMain) {
                setRangeMain(rangeNew);
              }
            }}
            renderRate={renderRate}
            fixedYRange={fixedYRange}
            fixedRatio={1}
          />
          {mouseEvent &&
            hoverTime &&
            mouseXMain &&
            filterFields.size > 0 && (
              <CursorLine
                style={{
                  left: mouseXMain + margin.left - 4,
                  height: "242px",
                  bottom: "30px",
                }}
              />
            )}
          {mouseEvent && hoverTime && filterFields.size > 0 && (
            <Tooltip
              dataPoint={tooltipData}
              mouseX={mouseEvent.mouseX}
              mouseY={mouseEvent.mouseY}
              title={formatDate(hoverTime)}
              mouseEvent={mouseEvent}
              chartWidth={width}
              fieldsMap={fieldsMap}
              filterFields={filterFields}
              type={type}
              unit="Mbps"
            />
          )}
        </ChartContainer>
      </ChartsWrapper>
    </Wrapper>
  );
};

export default LiveChart;
